import { api } from "@/api/client";

export interface RefOption {
  id: number;
  name: string;
}

export interface ItemOption extends RefOption {
  type: string;        // NetSuite itemtype, e.g. InvtPart / NonInvtPart / OthCharge
  type_label: string;  // "Inventory" / "Non-Inventory" / "Other Charge"
  vendor_code?: string | null; // NetSuite "Vendor Name/Code" — the vendor's part number
}

export interface TermOption extends RefOption {
  days: number | null; // NetSuite "days until net due"; null for e.g. "Due Upon Receipt"
}

export interface ReferenceData {
  departments: RefOption[];
  classes: RefOption[];
  locations: RefOption[];
  employees: RefOption[];
  terms: TermOption[];
  items: ItemOption[];
  accounts: RefOption[]; // GL accounts for expense lines (name = "50210 Solar : Panels")
}

export async function fetchReferenceData(): Promise<ReferenceData> {
  const { data } = await api.get<ReferenceData>("/reference_data");
  return data;
}

// A NetSuite Project (job). Too many to ship whole, so searched server-side.
export interface ProjectOption {
  id: number;         // job internal id — stored on a line as customer_id
  entityid: string;   // human project number, e.g. "C41742" / "S9914" / "117368"
  name: string;       // NetSuite companyname
  label: string;      // display, e.g. "C41742 — Boardwalk Acura"
}

// Fuzzy search by project number or customer name (min 2 chars).
export async function searchProjects(q: string): Promise<ProjectOption[]> {
  if (q.trim().length < 2) return [];
  const { data } = await api.get<ProjectOption[]>("/reference_data/projects", { params: { q } });
  return data;
}

// Resolve specific projects by internal id — used to label already-coded lines.
export async function fetchProjectsByIds(ids: Array<number | string>): Promise<ProjectOption[]> {
  const unique = Array.from(new Set(ids.map(String).filter((s) => s && s !== "null")));
  if (unique.length === 0) return [];
  const { data } = await api.get<ProjectOption[]>("/reference_data/projects", { params: { ids: unique.join(",") } });
  return data;
}

// A NetSuite vendor from the local mirror — for the dashboard's "Vendor" filter.
export interface VendorOption {
  id: number;
  name: string;
}

// Fuzzy vendor-name search (min 2 chars), busiest vendors first.
export async function searchVendors(q: string): Promise<VendorOption[]> {
  if (q.trim().length < 2) return [];
  const { data } = await api.get<VendorOption[]>("/reference_data/vendors", { params: { q } });
  return data;
}

// Resolve specific vendors by id — used to label a vendor filter set from the URL.
export async function fetchVendorsByIds(ids: Array<number | string>): Promise<VendorOption[]> {
  const unique = Array.from(new Set(ids.map(String).filter((s) => s && s !== "null")));
  if (unique.length === 0) return [];
  const { data } = await api.get<VendorOption[]>("/reference_data/vendors", { params: { ids: unique.join(",") } });
  return data;
}
